import { api } from "../services/api.js";
import { legacyClientId, LEGACY_CLIENT_EVENT } from "../services/legacy-client.js";
import { cardholderTermForClient } from "../services/terminology.js";
import type { CardholderTerm } from "../utils/terminology.js";
import type { LibraryContext } from "../types/library.js";

// Shared client scoping + context for the Library settings surfaces
// (branches / opening hours / branch groups / pickup locations —
// docs/branches-port-plan.md).
//
// THE LESSON THIS ENCODES (Attend A5, commit 934d025): a page must NEVER
// fetch the Communico-staff-only client picker. Library admins are the
// primary audience here; a page that loads /legacy/clients would 403
// every one of them before the module could gate anything. So:
//
//   - Communico staff pick a client in the shared Partner-nav picker;
//     `libraryClientId()` reads that choice.
//   - A library admin never picks anything — it reads 0, we send no
//     client_id, and the backend forces their own tenant.
//
// Neither path calls the picker endpoint from here.

export function libraryClientId(): number {
	return legacyClientId();
}

/** Fired when the shared Partner-nav picker changes. */
export const LIBRARY_CLIENT_EVENT = LEGACY_CLIENT_EVENT;

/**
 * The one typed bundle every Library surface loads first: the caller's
 * rights plus the client settings that gate which FIELDS render.
 * Resolving these up front rather than discovering them field by field
 * is the Attend round-1 lesson (port-plan §7.2).
 */
export function loadLibraryContext(): Promise<LibraryContext> {
	const cid = libraryClientId();
	return api.getLibraryContext(cid > 0 ? cid : undefined) as Promise<LibraryContext>;
}

/** Query scope for a list call — omitted entirely for a library admin. */
export function clientScope(): Record<string, unknown> {
	const cid = libraryClientId();
	return cid > 0 ? { client_id: cid } : {};
}

/** The client id to pass to a write helper (undefined = own tenant). */
export function writeScope(): number | undefined {
	const cid = libraryClientId();
	return cid > 0 ? cid : undefined;
}

/**
 * The library's own word for its end users, for any Library-settings page
 * that has already loaded its LibraryContext. `ctx.client_id` is always
 * the REAL resolved tenant (the server infers a library admin's own client
 * even though `libraryClientId()` reads 0 for them locally), so this needs
 * no self/client branch the way the COS partner_id-space surfaces do.
 */
export function libraryCardholderTerm(ctx: LibraryContext | null | undefined): Promise<CardholderTerm> | null {
	if (!ctx) return null;
	return cardholderTermForClient(ctx.client_id);
}

/**
 * A–Z index for a name list. Returns the letters actually present, so an
 * index never offers a letter that leads nowhere. Anything not starting
 * with a Latin letter buckets under "#".
 */
export function azBucket(name: string): string {
	const c = (name ?? "").trim().charAt(0).toUpperCase();
	return c >= "A" && c <= "Z" ? c : "#";
}

export function azLetters(names: string[]): string[] {
	const seen = new Set(names.map(azBucket));
	const out = Array.from(seen).sort();
	// "#" sorts before "A" by code point; keep it last so the index reads
	// A..Z then the rest.
	return [...out.filter((l) => l !== "#"), ...(seen.has("#") ? ["#"] : [])];
}
